import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, AlertCircle, Plus, Trash2 } from 'lucide-react'
import { Drawer } from '../../components/ui/Drawer'
import { getContent, saveContent, getContentList, type ContentEntity } from '../../lib/api/content'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'

/* ── format <-> storage helpers (see Ui.js / Date.js / Time.js / WeekDays.js) ── */
const centsToEuros = (c: unknown): string => {
  const n = Number(c)
  return Number.isFinite(n) && n !== 0 ? (n / 100).toFixed(2) : n === 0 ? '0.00' : ''
}
const eurosToCents = (e: string): number => {
  const n = parseFloat(String(e).replace(',', '.'))
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}
const isoToDate = (v: unknown): string => {
  const m = String(v ?? '').match(/^(\d{4}-\d{2}-\d{2})/)
  return m ? m[1] : ''
}
const dateToIso = (d: string): string => (d ? `${d}T00:00:00Z` : '')
const hhmm = (v: unknown): string => {
  const m = String(v ?? '').match(/(\d{2}:\d{2})/)
  return m ? m[1] : ''
}
const WEEKDAYS: [string, string][] = [
  ['1', 'Lun'],
  ['2', 'Mar'],
  ['3', 'Mer'],
  ['4', 'Gio'],
  ['5', 'Ven'],
  ['6', 'Sab'],
  ['7', 'Dom'],
]

type OverrideRow = { date: string; price: string; enabled: boolean }
type Form = {
  transportId: string
  price: string
  originalPrice: string
  hours: string
  priority: string
  enabled: boolean
  visibleOnFrontEnd: boolean
  overrideFromDay: string
  overrideFromTime: string
  validity: { fromDay: string; toDay: string; fromTime: string; toTime: string; weekDays: string[] }
  overrides: OverrideRow[]
}

function seedForm(el: El): Form {
  const ref = el.transportId
  const transportId =
    ref && typeof ref === 'object' ? String((ref as { id?: unknown }).id ?? '') : String(ref ?? '')
  const v = (el.validity ?? {}) as El
  const ov = Array.isArray(el.overrides) ? (el.overrides as El[]) : []
  return {
    transportId,
    price: centsToEuros(el.price),
    originalPrice: centsToEuros(el.originalPrice),
    hours: el.hours != null ? String(el.hours) : '',
    priority: el.priority != null ? String(el.priority) : '0',
    enabled: isYes(el.enabled),
    visibleOnFrontEnd: isYes(el.visibleOnFrontEnd),
    overrideFromDay: isoToDate(el.overrideFromDay),
    overrideFromTime: hhmm(el.overrideFromTime),
    validity: {
      fromDay: isoToDate(v.fromDay),
      toDay: isoToDate(v.toDay),
      fromTime: hhmm(v.fromTime),
      toTime: hhmm(v.toTime),
      weekDays: String(v.weekDays ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    },
    overrides: ov.map((o) => ({ date: isoToDate(o.date), price: centsToEuros(o.price), enabled: isYes(o.enabled) })),
  }
}

const TABS = ['Generale', 'Validità', 'Variazioni'] as const

/* ── small field atoms ── */
function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-[1.2px] text-muted">{label}</span>
      {children}
      {hint && <span className="mt-0.5 block text-[11px] text-faint">{hint}</span>}
    </label>
  )
}
const inputCls = 'h-9 w-full rounded-md border border-line bg-card px-3 text-[13px] text-coal outline-none focus:border-verdigris disabled:opacity-50'
function Toggle({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <label className="flex items-center gap-2 text-[13px] text-coal">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-verdigris" />
      {label}
    </label>
  )
}
function Euro({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <div className="flex h-9 items-center gap-1 rounded-md border border-line bg-card px-2.5 focus-within:border-verdigris">
      <span className="text-[12px] text-muted">€</span>
      <input type="number" step="0.01" min="0" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent text-right font-mono text-[13px] text-coal outline-none disabled:opacity-50" />
    </div>
  )
}

export function RateEditDrawer({ rateId, onClose }: { rateId: string | null; onClose: () => void }) {
  const qc = useQueryClient()
  const [tab, setTab] = useState(0)
  const [form, setForm] = useState<Form | null>(null)
  const [entity, setEntity] = useState<ContentEntity | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const detail = useQuery({
    queryKey: ['rate', rateId],
    queryFn: () => getContent('rate', rateId!),
    enabled: Boolean(rateId),
  })
  const transports = useQuery({
    queryKey: ['opt', 'transport'],
    queryFn: () => getContentList('transport', { limit: 50 }),
    staleTime: Infinity,
  })

  useEffect(() => {
    if (detail.data) {
      setEntity(detail.data)
      setForm(seedForm(detail.data.elements as El))
      setTab(0)
      setError(null)
    }
  }, [detail.data])

  const transportOptions = useMemo(
    () =>
      (transports.data?.data ?? []).map((t) => ({
        id: String(t.id),
        label: String((t.elements.name as string) ?? (t.elements.label as string) ?? t.id),
      })),
    [transports.data],
  )

  if (!rateId) return null

  const patch = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f))
  const patchValidity = (p: Partial<Form['validity']>) => setForm((f) => (f ? { ...f, validity: { ...f.validity, ...p } } : f))
  const patchOverride = (i: number, p: Partial<OverrideRow>) =>
    setForm((f) => (f ? { ...f, overrides: f.overrides.map((o, j) => (j === i ? { ...o, ...p } : o)) } : f))
  const toggleWeekday = (d: string) =>
    setForm((f) => {
      if (!f) return f
      const has = f.validity.weekDays.includes(d)
      const weekDays = has ? f.validity.weekDays.filter((x) => x !== d) : [...f.validity.weekDays, d].sort()
      return { ...f, validity: { ...f.validity, weekDays } }
    })

  async function save() {
    if (!form || !entity) return
    if (eurosToCents(form.price) <= 0) {
      setError('Inserisci un prezzo Online valido.')
      setTab(0)
      return
    }
    setError(null)
    setSaving(true)
    try {
      // round-trip: keep the full loaded entity, overwrite only the edited elements
      const next: ContentEntity = { ...entity, elements: { ...(entity.elements as El) } }
      const e = next.elements as El
      e.transportId = { id: Number(form.transportId), modelId: 'transport' }
      e.price = eurosToCents(form.price)
      e.originalPrice = eurosToCents(form.originalPrice)
      e.hours = Number(form.hours) || 0
      e.priority = Number(form.priority) || 0
      e.enabled = form.enabled
      e.visibleOnFrontEnd = form.visibleOnFrontEnd
      e.overrideFromDay = dateToIso(form.overrideFromDay)
      e.overrideFromTime = form.overrideFromTime || ''
      e.validity = {
        fromDay: dateToIso(form.validity.fromDay),
        toDay: dateToIso(form.validity.toDay),
        fromTime: form.validity.fromTime || '',
        toTime: form.validity.toTime || '',
        weekDays: form.validity.weekDays.join(','),
      }
      e.overrides = form.overrides
        .filter((o) => o.date || o.price)
        .map((o) => ({ date: dateToIso(o.date), price: eurosToCents(o.price), enabled: o.enabled }))
      await saveContent('rate', next)
      await qc.invalidateQueries({ queryKey: ['rates'] })
      await qc.invalidateQueries({ queryKey: ['rate', rateId] })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Salvataggio non riuscito.')
    } finally {
      setSaving(false)
    }
  }

  const transLabel = transportOptions.find((t) => t.id === form?.transportId)?.label ?? ''

  return (
    <Drawer
      open={Boolean(rateId)}
      onClose={onClose}
      tone="brand"
      title={<span className="text-[15px] font-bold text-white">Modifica tariffa</span>}
      subtitle={form ? `${transLabel} · ${form.hours || '—'} ore` : 'Caricamento…'}
      footer={
        <div className="flex items-center gap-3">
          {error ? (
            <span className="text-[12px] font-medium text-venetian">{error}</span>
          ) : (
            <span className="text-[11.5px] text-muted">Il salvataggio scrive sul gestionale live.</span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={onClose} disabled={saving} className="rounded-lg border border-line bg-card px-4 py-2 text-[13px] font-semibold text-slate hover:border-faint disabled:opacity-50">
              Annulla
            </button>
            <button onClick={save} disabled={saving || !form} className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-2 text-[13px] font-semibold text-white hover:bg-verdigris-700 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva
            </button>
          </div>
        </div>
      }
    >
      {detail.isLoading || !form ? (
        <div className="flex items-center justify-center py-20 text-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : detail.isError ? (
        <div className="py-20 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare la tariffa.
        </div>
      ) : (
        <>
          {/* tabs */}
          <div className="mb-5 flex gap-1 border-b border-line">
            {TABS.map((label, i) => (
              <button
                key={label}
                onClick={() => setTab(i)}
                className={cn(
                  '-mb-px border-b-2 px-3 py-2 text-[13px] font-semibold transition-colors',
                  tab === i ? 'border-verdigris text-coal' : 'border-transparent text-muted hover:text-coal',
                )}
              >
                {label}
                {i === 2 && form.overrides.length > 0 && (
                  <span className="ml-1.5 rounded-full bg-paper px-1.5 text-[11px] text-muted ring-1 ring-line">{form.overrides.length}</span>
                )}
              </button>
            ))}
          </div>

          {/* ── GENERALE ── */}
          {tab === 0 && (
            <div className="space-y-4">
              <Field label="Tipo di veicolo">
                <select className={inputCls} value={form.transportId} disabled={saving} onChange={(e) => patch({ transportId: e.target.value })}>
                  {transportOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Listino" hint="Importo simulazione sconto">
                  <Euro value={form.originalPrice} disabled={saving} onChange={(v) => patch({ originalPrice: v })} />
                </Field>
                <Field label="Online" hint="Importo effettivo di vendita">
                  <Euro value={form.price} disabled={saving} onChange={(v) => patch({ price: v })} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Durata (ore)">
                  <input type="number" min="0" className={inputCls} value={form.hours} disabled={saving} onChange={(e) => patch({ hours: e.target.value })} />
                </Field>
                <Field label="Priorità">
                  <input type="number" className={inputCls} value={form.priority} disabled={saving} onChange={(e) => patch({ priority: e.target.value })} />
                </Field>
              </div>
              <div className="flex flex-wrap gap-5 pt-1">
                <Toggle checked={form.enabled} disabled={saving} onChange={(v) => patch({ enabled: v })} label="Attiva" />
                <Toggle checked={form.visibleOnFrontEnd} disabled={saving} onChange={(v) => patch({ visibleOnFrontEnd: v })} label="Visibile sul sito" />
              </div>
              <div className="grid grid-cols-2 gap-3 border-t border-line-soft pt-4">
                <Field label="Giorno fisso di inizio" hint="opzionale">
                  <input type="date" className={inputCls} value={form.overrideFromDay} disabled={saving} onChange={(e) => patch({ overrideFromDay: e.target.value })} />
                </Field>
                <Field label="Ora fissa di inizio" hint="opzionale">
                  <input type="time" className={inputCls} value={form.overrideFromTime} disabled={saving} onChange={(e) => patch({ overrideFromTime: e.target.value })} />
                </Field>
              </div>
            </div>
          )}

          {/* ── VALIDITÀ ── */}
          {tab === 1 && (
            <div className="space-y-4">
              <p className="text-[12.5px] text-muted">Finestra di validità della tariffa. Lascia vuoto per “sempre valida”.</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Da">
                  <input type="date" className={inputCls} value={form.validity.fromDay} disabled={saving} onChange={(e) => patchValidity({ fromDay: e.target.value })} />
                </Field>
                <Field label="A">
                  <input type="date" className={inputCls} value={form.validity.toDay} disabled={saving} onChange={(e) => patchValidity({ toDay: e.target.value })} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Dalle ore">
                  <input type="time" className={inputCls} value={form.validity.fromTime} disabled={saving} onChange={(e) => patchValidity({ fromTime: e.target.value })} />
                </Field>
                <Field label="Fino alle ore">
                  <input type="time" className={inputCls} value={form.validity.toTime} disabled={saving} onChange={(e) => patchValidity({ toTime: e.target.value })} />
                </Field>
              </div>
              <Field label="Giorni della settimana">
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAYS.map(([val, lbl]) => {
                    const on = form.validity.weekDays.includes(val)
                    return (
                      <button
                        key={val}
                        type="button"
                        disabled={saving}
                        onClick={() => toggleWeekday(val)}
                        className={cn('rounded-md border px-2.5 py-1.5 text-[12px] font-semibold transition-colors', on ? 'border-verdigris bg-verdigris text-white' : 'border-line bg-card text-muted hover:text-coal')}
                      >
                        {lbl}
                      </button>
                    )
                  })}
                </div>
              </Field>
            </div>
          )}

          {/* ── VARIAZIONI ── */}
          {tab === 2 && (
            <div className="space-y-3">
              <p className="text-[12.5px] text-muted">Prezzi speciali per date puntuali (eventi, weekend). Sostituiscono l’Online in quel giorno.</p>
              {form.overrides.length === 0 && <p className="rounded-lg bg-paper py-6 text-center text-[13px] text-muted">Nessuna variazione.</p>}
              {form.overrides.map((o, i) => (
                <div key={i} className="flex items-end gap-2 rounded-lg border border-line bg-card p-2.5">
                  <Field label="Data">
                    <input type="date" className={inputCls} value={o.date} disabled={saving} onChange={(e) => patchOverride(i, { date: e.target.value })} />
                  </Field>
                  <div className="w-28">
                    <Field label="Prezzo">
                      <Euro value={o.price} disabled={saving} onChange={(v) => patchOverride(i, { price: v })} />
                    </Field>
                  </div>
                  <label className="flex h-9 items-center gap-1.5 px-1 text-[12px] text-muted">
                    <input type="checkbox" checked={o.enabled} disabled={saving} onChange={(e) => patchOverride(i, { enabled: e.target.checked })} className="h-4 w-4 accent-verdigris" />
                    Attiva
                  </label>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => patch({ overrides: form.overrides.filter((_, j) => j !== i) })}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-line text-muted hover:border-venetian hover:text-venetian"
                    title="Rimuovi"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                disabled={saving}
                onClick={() => patch({ overrides: [...form.overrides, { date: '', price: '', enabled: true }] })}
                className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-line px-3 py-2 text-[13px] font-semibold text-slate hover:border-faint"
              >
                <Plus className="h-4 w-4" /> Aggiungi variazione
              </button>
            </div>
          )}
        </>
      )}
    </Drawer>
  )
}
